/**
 * Ticket Variants Marketplace JavaScript
 * Handles cart functionality for the ticket variants display
 */

(function() {
  'use strict';
  
  // Cache DOM queries
  let marketplace = null;
  let cartButtons = [];
  let isProcessing = false;
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  function init() {
    marketplace = document.querySelector('.ticket-marketplace-wrapper');
    if (!marketplace) return;
    
    // Cache all buttons and inputs
    cartButtons = Array.from(marketplace.querySelectorAll('.cart-button'));
    
    // Setup event delegation
    marketplace.addEventListener('click', handleMarketplaceClick, { passive: false });
    marketplace.addEventListener('change', handleQuantityChange, { passive: true });
  }
  
  // Single event handler for all clicks
  function handleMarketplaceClick(e) {
    const target = e.target;
    
    // Handle quantity buttons
    if (target.classList.contains('qty-decrease') || target.classList.contains('qty-increase')) {
      e.preventDefault();
      handleQuantityButton(target);
      return;
    }
    
    // Handle add to cart
    const cartButton = target.closest('.cart-button');
    if (cartButton && !cartButton.disabled) {
      e.preventDefault();
      e.stopImmediatePropagation(); // CRITICAL: Stop other scripts from also adding to cart
      addToCart(cartButton);
    }
  }
  
  // Handle quantity button clicks
  function handleQuantityButton(button) {
    const variantId = button.getAttribute('data-variant-id');
    const card = button.closest('.ticket-variant-card');
    // Check for both input and select (quantity dropdown may replace input with select)
    const input = card.querySelector('.qty-value') || card.querySelector('input.qty-value, select.qty-value');
    
    if (!input) return;
    
    const currentValue = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || 999;
    
    if (button.classList.contains('qty-decrease')) {
      if (currentValue > min) {
        input.value = currentValue - 1;
      }
    } else {
      if (currentValue < max) {
        input.value = currentValue + 1;
      }
    }
  }
  
  // Handle direct quantity input changes
  function handleQuantityChange(e) {
    if (!e.target.classList.contains('qty-value')) return;
    
    const input = e.target;
    const value = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || 999;
    
    // Clamp value between min and max
    input.value = Math.max(min, Math.min(max, value));
  }
  
  // Add to cart with proper cart drawer update
  async function addToCart(button) {
    // Prevent double clicks
    if (button.disabled || button.classList.contains('loading')) {
      return;
    }
    
    const variantId = button.getAttribute('data-variant-id');
    const card = button.closest('.ticket-variant-card');
    // Check for both input and select (quantity dropdown may replace input with select)
    const quantityInput = card.querySelector('.qty-value') || card.querySelector('input.qty-value, select.qty-value');
    const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
    console.log('[TicketVariantsMarketplace] Quantity selected:', quantity);
    
    // Show loading state
    button.disabled = true;
    button.classList.add('loading');
    const originalContent = button.innerHTML;
    button.innerHTML = '<span style="display: inline-block; animation: spin 0.6s linear infinite;">⊙</span> Adding...';
    
    try {
      // Add to cart with sections for drawer update
      const formData = {
        items: [{
          id: variantId,
          quantity: parseInt(quantity)
        }]
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Success animation
      button.classList.remove('loading');
      button.classList.add('success');
      button.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg><span>Added!</span>';
      
      // Update cart bubble and drawer
      await updateCartBubble();
      await refreshCartDrawer();
      
      // Open cart drawer after a short delay
      setTimeout(() => {
        openCartDrawer();
      }, 300);
      
      // Reset button after animation
      setTimeout(() => {
        button.classList.remove('success');
        button.innerHTML = originalContent;
        button.disabled = false;
        
        // Reset quantity to 1
        if (quantityInput) {
          quantityInput.value = 1;
        }
      }, 2000);
      
      // Show notification
      showNotification(`Added ${quantity} ticket${quantity > 1 ? 's' : ''} to cart!`, 'success');
      
    } catch (error) {
      console.error('Cart error:', error);
      
      button.classList.remove('loading');
      button.innerHTML = originalContent;
      button.disabled = false;
      
      showNotification('Failed to add to cart. Please try again.', 'error');
    }
  }
  
  // Update cart bubble count
  async function updateCartBubble() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });
      const cart = await response.json();
      
      // Update the cart-count custom element (primary header cart icon)
      const cartCountElement = document.querySelector('cart-count');
      if (cartCountElement) {
        // Update the visible count
        const countSpan = cartCountElement.querySelector('span[aria-hidden="true"]');
        if (countSpan) {
          countSpan.textContent = cart.item_count;
        }
        
        // Update screen reader text
        const srSpan = cartCountElement.querySelector('.sr-only');
        if (srSpan) {
          srSpan.textContent = srSpan.textContent.replace(/\d+/, cart.item_count);
        }
        
        // Show/hide the count bubble based on item count
        if (cart.item_count > 0) {
          cartCountElement.classList.remove('opacity-0');
        } else {
          cartCountElement.classList.add('opacity-0');
        }
      }
      
      // Update all other cart count elements (for backward compatibility)
      document.querySelectorAll('.cart-count-bubble, .cart-count, .cart-count__bubble').forEach(element => {
        if (element && element.tagName !== 'CART-COUNT') {
          const countSpan = element.querySelector('span') || element;
          countSpan.textContent = cart.item_count;
          
          // Show/hide based on count
          if (cart.item_count > 0) {
            element.classList.remove('hide');
            element.style.display = '';
          } else {
            element.classList.add('hide');
          }
        }
      });
      
      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cart }
      }));
    } catch (error) {
      console.error('Error updating cart bubble:', error);
    }
  }
  
  // Refresh cart drawer content
  async function refreshCartDrawer() {
    try {
      // Fetch the cart sections
      const response = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
      const sections = await response.json();
      
      // Update cart drawer HTML if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && sections['cart-drawer']) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sections['cart-drawer'];
        const newCartContent = tempDiv.querySelector('cart-drawer');
        if (newCartContent) {
          cartDrawer.innerHTML = newCartContent.innerHTML;
        }
      }
      
      // Update cart icon bubble
      if (sections['cart-icon-bubble']) {
        const cartIconBubble = document.querySelector('.header__icon--cart');
        if (cartIconBubble) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections['cart-icon-bubble'];
          const newBubble = tempDiv.querySelector('.cart-count-bubble');
          const currentBubble = cartIconBubble.querySelector('.cart-count-bubble');
          if (newBubble && currentBubble) {
            currentBubble.replaceWith(newBubble);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }
  
  // Open cart drawer
  function openCartDrawer() {
    // Dispatch cart drawer open event
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    
    // Try to open cart drawer component
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else if (cartDrawer.hasAttribute('open')) {
        cartDrawer.setAttribute('open', '');
      }
    }
    
    // Or trigger click on cart icon
    const cartIcon = document.querySelector('[aria-controls="cart-drawer"], .header__icon--cart, .cart-link');
    if (cartIcon) {
      cartIcon.click();
    }
  }
  
  // Show notification
  function showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `ticket-notification ${type}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#4CAF50' : '#f44336'};
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 99999;
      display: flex;
      align-items: center;
      gap: 12px;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease;
    `;
    
    notification.innerHTML = `
      ${type === 'success' ? 
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>' :
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
      }
      <span>${message}</span>
    `;
    
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    }, 10);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
  
  // Add spinning animation CSS if not already present
  if (!document.querySelector('#ticket-spinner-style')) {
    const style = document.createElement('style');
    style.id = 'ticket-spinner-style';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .cart-button.success {
        background-color: #4CAF50 !important;
      }
      .cart-button.loading {
        opacity: 0.7;
      }
    `;
    document.head.appendChild(style);
  }
})();